import { useState } from "react";
import { ViewHeader } from "./PortalShared";
import { Calculator, RefreshCw } from "lucide-react";
import { Input } from "./ui/input";

function fmt(n) {
  if (!n && n !== 0) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function calcMonthly(principal, annualRate, termMonths) {
  if (!principal || !annualRate || !termMonths) return 0;
  const r = annualRate / 100 / 12;
  const n = termMonths;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export default function LoanCalculator() {
  const [form, setForm] = useState({
    purchase_price: "", arv: "", loan_amount: "",
    interest_rate: "12", term_months: "12",
    origination_points: "2",
  });
  const [result, setResult] = useState(null);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const calculate = () => {
    const price = parseFloat(form.purchase_price) || 0;
    const arv = parseFloat(form.arv) || 0;
    const loan = parseFloat(form.loan_amount) || 0;
    const rate = parseFloat(form.interest_rate) || 0;
    const term = parseInt(form.term_months) || 12;
    const points = parseFloat(form.origination_points) || 0;

    const ltv = price > 0 ? ((loan / price) * 100).toFixed(1) : 0;
    const ltarv = arv > 0 ? ((loan / arv) * 100).toFixed(1) : 0;
    const monthly = calcMonthly(loan, rate, term);
    const totalInterest = monthly * term - loan;
    const originationFee = (loan * points) / 100;
    const totalCost = totalInterest + originationFee;

    setResult({ ltv, ltarv, monthly, totalInterest, originationFee, totalCost, loan, term });
  };

  return (
    <div>
      <ViewHeader title="Loan Calculator" subtitle="Bridge loan analysis and payment projections" />
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Loan Parameters</p>
          {[
            ["purchase_price","Purchase Price","number","$"],
            ["arv","After Repair Value (ARV)","number","$"],
            ["loan_amount","Loan Amount Requested","number","$"],
            ["interest_rate","Annual Interest Rate",  "number","%"],
            ["term_months","Loan Term (months)","number","mo"],
            ["origination_points","Origination Points","number","pts"],
          ].map(([k,l,t,suffix]) => (
            <div key={k}>
              <label className="text-[10px] text-white/40 block mb-1">{l}</label>
              <div className="relative">
                <Input type={t} value={form[k]} onChange={e => f(k, e.target.value)}
                  className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-xs">{suffix}</span>
              </div>
            </div>
          ))}
          <button onClick={calculate}
            className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm rounded transition-all flex items-center justify-center gap-2"
            data-testid="calculate-loan-btn">
            <Calculator className="w-4 h-4" /> Calculate
          </button>
        </div>
        <div className="space-y-4">
          {result ? (
            <>
              <div className="bg-[#0A0A0A] border border-purple-500/20 rounded-xl p-5">
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-4">Loan Analysis</p>
                <div className="space-y-3">
                  {[
                    { label: "Monthly Payment", value: fmt(result.monthly), color: "text-purple-400", large: true },
                    { label: "LTV (Purchase)", value: `${result.ltv}%`, color: result.ltv > 80 ? "text-[#FF3B30]" : "text-[#00FF88]" },
                    { label: "LT-ARV", value: `${result.ltarv}%`, color: result.ltarv > 75 ? "text-[#FF9F0A]" : "text-[#00FF88]" },
                    { label: "Total Interest", value: fmt(result.totalInterest), color: "text-white" },
                    { label: "Origination Fee", value: fmt(result.originationFee), color: "text-[#D4AF37]" },
                    { label: "Total Cost of Capital", value: fmt(result.totalCost), color: "text-white", bold: true },
                  ].map(({ label, value, color, large, bold }) => (
                    <div key={label} className="flex items-center justify-between border-b border-white/[0.04] pb-2">
                      <span className="text-white/50 text-sm">{label}</span>
                      <span className={`font-${bold ? 'bold' : 'medium'} ${large ? 'text-2xl' : 'text-sm'} ${color}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Cost Breakdown</p>
                <div className="space-y-2">
                  {[
                    { label: "Interest", amount: result.totalInterest, pct: result.totalCost > 0 ? (result.totalInterest / result.totalCost) * 100 : 0, color: "bg-purple-500" },
                    { label: "Origination", amount: result.originationFee, pct: result.totalCost > 0 ? (result.originationFee / result.totalCost) * 100 : 0, color: "bg-[#D4AF37]" },
                  ].map(({ label, amount, pct, color }) => (
                    <div key={label}>
                      <div className="flex justify-between text-xs text-white/40 mb-1">
                        <span>{label}</span><span>{fmt(amount)}</span>
                      </div>
                      <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-20 bg-white/[0.01] border border-white/[0.05] rounded-xl">
              <Calculator className="w-12 h-12 text-purple-400/30 mb-4" />
              <p className="text-white/30 text-sm">Enter loan parameters and click Calculate</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
